import React, { useEffect, useRef, useState } from "react";
import {
    Stethoscope,
    Video,
    CheckCircle2,
    Mic,
    PhoneOff,
    MicOff,
    VideoOff,
    Volume2,
    CalendarClock,
    User,
    MessageSquareReply,
} from "lucide-react";
import { useClara } from "../contexts/ClaraContext";
import { useData } from "../contexts/DataContext";
import { APPOINTMENT } from "../data/sampleData";
import ClaraListening from "../components/ClaraListening";
import Avatar from "../components/Avatar";
import VoiceReply from "../components/VoiceReply";

const STAGES = {
    APPOINTMENT: "appointment",
    PERMISSION: "permission",
    CAMERA: "camera",
    MIC: "mic",
    IN_CALL: "in_call",
};

const CAPTION_LINES = [
    "Hi Maria, lovely to see you today. How are you feeling?",
    "Your glucose log looks great — nice and steady.",
    "Your blood pressure has been right in range, well done.",
    "Any new symptoms you'd like to talk about?",
];

export const Telehealth = ({ onBackHome }) => {
    const { speak } = useClara();
    const { addReply, repliesByMessage } = useData();
    const [stage, setStage] = useState(STAGES.APPOINTMENT);
    const [seconds, setSeconds] = useState(0);
    const [caption, setCaption] = useState(CAPTION_LINES[0]);
    const captionIndexRef = useRef(0);
    const [muted, setMuted] = useState(false);
    const [cameraOn, setCameraOn] = useState(true);
    const [notingDoctor, setNotingDoctor] = useState(false);
    const doctorNotes = repliesByMessage["doctor-note"] || [];

    useEffect(() => {
        if (stage === STAGES.APPOINTMENT) {
            speak(
                `You have an upcoming appointment with ${APPOINTMENT.doctor}, ${APPOINTMENT.specialty}, ${APPOINTMENT.when}.`,
            );
        }
    }, [stage, speak]);

    // In-call timer + caption cycling
    useEffect(() => {
        if (stage !== STAGES.IN_CALL) return;
        const timer = setInterval(() => setSeconds((s) => s + 1), 1000);
        const captionTimer = setInterval(() => {
            captionIndexRef.current =
                (captionIndexRef.current + 1) % CAPTION_LINES.length;
            setCaption(CAPTION_LINES[captionIndexRef.current]);
        }, 5500);
        return () => {
            clearInterval(timer);
            clearInterval(captionTimer);
        };
    }, [stage]);

    const hearAppointmentDetails = () => {
        speak(
            `Your appointment is with ${APPOINTMENT.doctor}, ${APPOINTMENT.specialty}, ${APPOINTMENT.when}. Your glucose and blood pressure logs are ready to share.`,
        );
    };

    const startJoin = () => {
        speak(
            `Your appointment with Doctor Patel is starting. I need permission to use your camera and microphone for the video call. Please listen for the permission prompt.`,
        );
        setStage(STAGES.PERMISSION);
    };

    const givePermission = () => {
        speak(`Thank you. Checking your camera now.`);
        setStage(STAGES.CAMERA);
    };

    const cameraNext = () => {
        speak(`I can see you clearly. You look great. Now checking your microphone.`);
        setStage(STAGES.MIC);
    };

    const micNext = () => {
        speak(`Microphone is working perfectly. Joining your appointment now.`);
        setSeconds(0);
        setCaption(CAPTION_LINES[0]);
        captionIndexRef.current = 0;
        setStage(STAGES.IN_CALL);
    };

    const endCall = () => {
        speak(`Call ended. I've saved a summary to your health journal.`);
        setStage(STAGES.APPOINTMENT);
    };

    const toggleMute = () => {
        const next = !muted;
        setMuted(next);
        speak(next ? "Microphone muted." : "Microphone unmuted.");
    };

    const toggleCamera = () => {
        const next = !cameraOn;
        setCameraOn(next);
        speak(next ? "Camera turned on." : "Camera turned off.");
    };

    const fmt = (s) =>
        `${Math.floor(s / 60)
            .toString()
            .padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;

    // STAGE 1
    if (stage === STAGES.APPOINTMENT) {
        return (
            <div className="space-y-5">
                <section
                    data-testid="appointment-card"
                    className="bg-white rounded-3xl overflow-hidden border border-[#0A3D7A]/10 shadow-[0_4px_20px_rgba(10,61,122,0.06)]"
                >
                    <div className="p-6 bg-gradient-to-br from-[#0A3D7A] to-[#1B5BA8] text-white">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-[#E0C875]">
                            <CalendarClock className="w-4 h-4" aria-hidden="true" />
                            Upcoming visit
                        </div>
                        <h2 className="font-display text-2xl font-bold mt-1">
                            {APPOINTMENT.when}
                        </h2>
                        <div className="mt-4 flex items-center gap-3">
                            <Avatar
                                seed={APPOINTMENT.avatarSeed}
                                name={APPOINTMENT.doctor}
                                size={56}
                                ring={false}
                                className="border-2 border-white"
                            />
                            <div>
                                <p className="font-bold text-lg leading-tight">
                                    {APPOINTMENT.doctor}
                                </p>
                                <p className="text-white/90 text-sm">
                                    {APPOINTMENT.specialty}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="p-6">
                        <p className="text-xs uppercase tracking-wider font-semibold text-[#5A5A6E] mb-3">
                            Pre-visit checklist
                        </p>
                        <ul className="space-y-2 mb-5">
                            {APPOINTMENT.preVisit.map((p) => (
                                <li
                                    key={p.id}
                                    data-testid={`previsit-${p.id}`}
                                    className="flex items-center gap-2 text-sm text-[#1A1A2E]"
                                >
                                    <CheckCircle2
                                        className="w-5 h-5 text-[#0A5C36]"
                                        aria-hidden="true"
                                    />
                                    <span>{p.label}</span>
                                    <span className="ml-auto text-xs font-bold uppercase tracking-wider text-[#0A5C36]">
                                        Ready
                                    </span>
                                </li>
                            ))}
                        </ul>

                        <div className="space-y-3">
                            <button
                                type="button"
                                onClick={startJoin}
                                data-testid="join-appointment-btn"
                                className="w-full min-h-[56px] rounded-2xl bg-[#0A5C36] hover:bg-[#0a4d2d] text-white text-lg font-bold flex items-center justify-center gap-2 shadow-sm transition-colors"
                            >
                                <Video className="w-5 h-5" aria-hidden="true" />
                                Join Appointment
                            </button>
                            <button
                                type="button"
                                onClick={hearAppointmentDetails}
                                data-testid="hear-appointment-btn"
                                className="w-full min-h-[56px] rounded-2xl bg-white border-2 border-[#0A3D7A] text-[#0A3D7A] text-lg font-bold flex items-center justify-center gap-2 hover:bg-[#0A3D7A]/5 transition-colors"
                            >
                                <Volume2 className="w-5 h-5" aria-hidden="true" />
                                Hear Appointment Details
                            </button>
                            <button
                                type="button"
                                onClick={() => setNotingDoctor(true)}
                                data-testid="send-doctor-note-btn"
                                aria-label={`Send Doctor Patel a note by voice`}
                                className="w-full min-h-[56px] rounded-2xl bg-white border-2 border-[#0A3D7A] text-[#0A3D7A] text-lg font-bold flex items-center justify-center gap-2 hover:bg-[#0A3D7A]/5 transition-colors"
                            >
                                <MessageSquareReply className="w-5 h-5" aria-hidden="true" />
                                Send doctor a note
                            </button>
                        </div>
                    </div>
                </section>

                {notingDoctor && (
                    <VoiceReply
                        recipient={APPOINTMENT.doctor}
                        threadId="doctor-note"
                        onSent={(p) => addReply("doctor-note", p.text)}
                        onClose={() => setNotingDoctor(false)}
                    />
                )}

                {doctorNotes.length > 0 && (
                    <section
                        data-testid="doctor-notes-sent"
                        className="bg-white rounded-3xl p-5 border border-[#0A5C36]/20 shadow-[0_2px_12px_rgba(10,92,54,0.06)]"
                    >
                        <p className="text-xs uppercase tracking-wider font-semibold text-[#0A5C36] mb-3 flex items-center gap-1.5">
                            <CheckCircle2 className="w-4 h-4" aria-hidden="true" />
                            Notes sent to {APPOINTMENT.doctor}
                        </p>
                        <ul className="space-y-2">
                            {doctorNotes.map((n) => (
                                <li
                                    key={n.id}
                                    data-testid={`doctor-note-${n.id}`}
                                    className="rounded-xl bg-[#E5F1EB] border border-[#0A5C36]/20 p-3 text-sm text-[#1A1A2E] leading-snug whitespace-pre-wrap"
                                >
                                    {n.text}
                                </li>
                            ))}
                        </ul>
                    </section>
                )}
            </div>
        );
    }

    // STAGE 2 - permission
    if (stage === STAGES.PERMISSION) {
        return (
            <ClaraListening
                title="Clara is listening"
                subtitle={`Please say "I give permission" to start your visit with ${APPOINTMENT.doctor}.`}
                primaryAction={givePermission}
                primaryLabel='"I give permission"'
                primaryTestId="permission-give-btn"
                onCancel={() => {
                    speak("Cancelled. Returning to your appointment.");
                    setStage(STAGES.APPOINTMENT);
                }}
            />
        );
    }

    // STAGE 3 - camera check
    if (stage === STAGES.CAMERA) {
        return (
            <section
                data-testid="camera-check"
                className="bg-white rounded-3xl p-6 border border-[#0A3D7A]/10 shadow-[0_4px_20px_rgba(10,61,122,0.06)] fade-in-up"
            >
                <p className="text-xs tracking-[0.22em] uppercase font-semibold text-[#B8860B] mb-1">
                    Step 1 of 2
                </p>
                <h2 className="font-display text-2xl font-bold text-[#1A1A2E]">
                    Checking your camera
                </h2>
                <div className="mt-5 aspect-video rounded-2xl bg-[#0A3D7A] relative overflow-hidden flex items-center justify-center">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#0A3D7A] to-[#1A1A2E]" />
                    <div className="relative w-28 h-28 rounded-full bg-[#F0EAD6] border-4 border-white flex items-center justify-center shadow-xl">
                        <User
                            className="w-14 h-14 text-[#0A3D7A]"
                            aria-hidden="true"
                            strokeWidth={2}
                        />
                    </div>
                    <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-[#0A5C36] text-white text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
                        <Video className="w-3.5 h-3.5" />
                        Camera on
                    </span>
                </div>
                <p className="mt-5 text-[#1A1A2E] text-lg font-semibold leading-snug flex items-start gap-2">
                    <CheckCircle2
                        className="w-6 h-6 text-[#0A5C36] mt-0.5 shrink-0"
                        aria-hidden="true"
                    />
                    I can see you clearly. You look great!
                </p>
                <button
                    type="button"
                    onClick={cameraNext}
                    data-testid="camera-continue-btn"
                    className="mt-5 w-full min-h-[56px] rounded-2xl bg-[#0A3D7A] hover:bg-[#082F5E] text-white text-lg font-bold transition-colors"
                >
                    Continue
                </button>
            </section>
        );
    }

    // STAGE 4 - mic check
    if (stage === STAGES.MIC) {
        return (
            <section
                data-testid="mic-check"
                className="bg-white rounded-3xl p-6 border border-[#0A3D7A]/10 shadow-[0_4px_20px_rgba(10,61,122,0.06)] fade-in-up"
            >
                <p className="text-xs tracking-[0.22em] uppercase font-semibold text-[#B8860B] mb-1">
                    Step 2 of 2
                </p>
                <h2 className="font-display text-2xl font-bold text-[#1A1A2E]">
                    Checking your microphone
                </h2>

                <div className="mt-6 flex items-center justify-center">
                    <div className="relative w-24 h-24 rounded-full bg-[#0A5C36] flex items-center justify-center shadow-xl">
                        <span
                            aria-hidden="true"
                            className="absolute inset-0 rounded-full bg-[#0A5C36]/60 pulse-ring"
                        />
                        <Mic className="relative w-11 h-11 text-white" aria-hidden="true" strokeWidth={2.5} />
                    </div>
                </div>

                <div
                    className="mt-6 flex items-end justify-center gap-2 h-20"
                    aria-hidden="true"
                >
                    {[0, 100, 200, 300, 400, 300, 200, 100, 0].map((d, i) => (
                        <span
                            key={`mic-bar-${i}-${d}`}
                            className="voice-bar w-3 h-full rounded-full bg-[#0A5C36]"
                            style={{ animationDelay: `${d}ms` }}
                        />
                    ))}
                </div>

                <p className="mt-6 text-[#1A1A2E] text-lg font-semibold leading-snug flex items-start gap-2">
                    <CheckCircle2
                        className="w-6 h-6 text-[#0A5C36] mt-0.5 shrink-0"
                        aria-hidden="true"
                    />
                    Microphone is working perfectly.
                </p>
                <button
                    type="button"
                    onClick={micNext}
                    data-testid="mic-continue-btn"
                    className="mt-5 w-full min-h-[56px] rounded-2xl bg-[#0A5C36] hover:bg-[#0a4d2d] text-white text-lg font-bold transition-colors"
                >
                    Join Doctor Patel
                </button>
            </section>
        );
    }

    // STAGE 5 - in-call
    return (
        <section
            data-testid="in-call-screen"
            className="rounded-3xl overflow-hidden bg-[#1A1A2E] text-white shadow-xl fade-in-up"
        >
            <div className="relative aspect-[4/5] bg-gradient-to-b from-[#0A3D7A] to-[#1A1A2E]">
                <div className="absolute inset-0 flex flex-col items-center justify-center p-6">
                    <Avatar
                        seed={APPOINTMENT.avatarSeed}
                        name={APPOINTMENT.doctor}
                        size={128}
                        ring={false}
                        className="border-4 border-white/30 shadow-2xl mb-4"
                    />
                    <p className="font-display font-bold text-2xl">
                        {APPOINTMENT.doctor}
                    </p>
                    <p className="text-white/80 text-sm">{APPOINTMENT.specialty}</p>
                </div>

                <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-[#0A5C36] text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
                    <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                    Live · {fmt(seconds)}
                </div>

                <div className="absolute bottom-4 right-4 w-24 h-32 rounded-xl border-2 border-white/40 bg-[#0A3D7A] overflow-hidden flex items-center justify-center">
                    {cameraOn ? (
                        <Stethoscope
                            className="w-10 h-10 text-white/70"
                            aria-hidden="true"
                        />
                    ) : (
                        <VideoOff
                            className="w-8 h-8 text-white/60"
                            aria-hidden="true"
                        />
                    )}
                </div>

                <div
                    className="absolute left-4 right-32 bottom-4 bg-black/70 backdrop-blur-sm text-white text-sm rounded-2xl p-3 border border-white/15"
                    data-testid="call-caption"
                    aria-live="polite"
                >
                    <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#E0C875] mb-0.5">
                        {APPOINTMENT.doctor}
                    </p>
                    <p className="leading-snug">{caption}</p>
                </div>
            </div>

            <div className="p-5 bg-[#0F0F1E] flex items-center justify-center gap-4">
                <CallButton
                    onClick={toggleMute}
                    label={muted ? "Unmute microphone" : "Mute microphone"}
                    testId="call-mute-btn"
                    color="bg-white/10 hover:bg-white/20 text-white"
                >
                    {muted ? (
                        <MicOff className="w-6 h-6" aria-hidden="true" />
                    ) : (
                        <Mic className="w-6 h-6" aria-hidden="true" />
                    )}
                </CallButton>
                <CallButton
                    onClick={endCall}
                    label="End call"
                    testId="call-end-btn"
                    color="bg-[#A31621] hover:bg-[#7c101a] text-white"
                    large
                >
                    <PhoneOff className="w-7 h-7" aria-hidden="true" />
                </CallButton>
                <CallButton
                    onClick={toggleCamera}
                    label={cameraOn ? "Turn camera off" : "Turn camera on"}
                    testId="call-camera-btn"
                    color="bg-white/10 hover:bg-white/20 text-white"
                >
                    {cameraOn ? (
                        <Video className="w-6 h-6" aria-hidden="true" />
                    ) : (
                        <VideoOff className="w-6 h-6" aria-hidden="true" />
                    )}
                </CallButton>
            </div>
        </section>
    );
};

const CallButton = ({ onClick, label, testId, color, children, large }) => (
    <button
        type="button"
        onClick={onClick}
        aria-label={label}
        data-testid={testId}
        className={`rounded-full ${large ? "w-16 h-16" : "w-14 h-14"} flex items-center justify-center ${color} transition-colors shadow-sm`}
    >
        {children}
    </button>
);

export default Telehealth;
